import React from "react";

/**
 * Top-level error boundary.
 *
 * Without this, a malformed report document white-screens the whole app with no
 * recovery path and no signal that anything went wrong. Class component because
 * componentDidCatch has no hooks equivalent.
 *
 * Wire the reporting hook in componentDidCatch once analytics lands (Commit C).
 */
export default class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { error: null };
  }

  static getDerivedStateFromError(error) {
    return { error };
  }

  componentDidCatch(error, info) {
    // eslint-disable-next-line no-console
    console.error("Uncaught error:", error, info?.componentStack);
    // Commit C: forward to analytics/error reporting here.
  }

  handleReset = () => {
    this.setState({ error: null });
  };

  render() {
    if (!this.state.error) return this.props.children;

    const isDev = process.env.NODE_ENV === "development";

    return (
      <div
        className="min-h-screen flex items-center justify-center px-6"
        style={{ backgroundColor: "#e8e4df" }}
        data-testid="error-boundary"
      >
        <div
          className="w-full max-w-lg p-8 md:p-10"
          style={{ backgroundColor: "#f5f2ed", border: "1px solid #ddd8ce" }}
        >
          <div
            className="mb-4"
            style={{
              fontFamily: "'JetBrains Mono', monospace",
              fontSize: 10,
              letterSpacing: "0.14em",
              textTransform: "uppercase",
              color: "#9a3412",
            }}
          >
            Something broke
          </div>

          <h1
            className="text-3xl md:text-4xl leading-tight mb-4"
            style={{
              fontFamily: "'Fraunces', 'Times New Roman', serif",
              fontVariationSettings: '"opsz" 96',
              letterSpacing: "-0.024em",
              fontWeight: 400,
              color: "#171717",
            }}
          >
            This page failed to render.
          </h1>

          <p
            className="mb-8 leading-relaxed"
            style={{
              fontFamily: "'JetBrains Mono', monospace",
              fontSize: 13,
              color: "#525252",
            }}
          >
            The error has been logged. Your sweep data is unaffected - reports are stored
            server-side and reloading will not lose anything.
          </p>

          {isDev && (
            <pre
              className="mb-8 p-4 overflow-x-auto"
              style={{
                backgroundColor: "#eeeae2",
                border: "1px solid #ddd8ce",
                fontFamily: "'JetBrains Mono', monospace",
                fontSize: 11,
                color: "#525252",
                whiteSpace: "pre-wrap",
              }}
            >
              {String(this.state.error?.stack || this.state.error)}
            </pre>
          )}

          <div className="flex flex-wrap gap-3">
            <button
              onClick={this.handleReset}
              data-testid="error-boundary-retry"
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 10,
                padding: "12px 22px",
                background: "#171717",
                color: "#f5f2ed",
                border: "1px solid #171717",
                borderRadius: 999,
                fontFamily: "'JetBrains Mono', monospace",
                fontSize: 12,
                letterSpacing: "0.08em",
                cursor: "pointer",
              }}
            >
              Try again
            </button>
            <a
              href="/"
              data-testid="error-boundary-home"
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 10,
                padding: "12px 22px",
                background: "transparent",
                color: "#171717",
                border: "1px solid #c9c3b8",
                borderRadius: 999,
                fontFamily: "'JetBrains Mono', monospace",
                fontSize: 12,
                letterSpacing: "0.08em",
                textDecoration: "none",
              }}
            >
              Back to home
            </a>
          </div>
        </div>
      </div>
    );
  }
}
